import * as globals from '@wandb/weave/common/css/globals.styles';
import React, {memo} from 'react';

import {ErrorBox} from '../../../../../components/ErrorBox';
import {captureError} from '../../../../../integrations';
import {getUsagePercent} from '../../util';
import * as S from './UsageCardBase.styles';

export enum LimitedResourceType {
  USER = 'Users',
  TRACKED_HOUR = 'Tracked hours',
  CUMULATIVE_TRACKED_HOUR = 'Total cumulative tracked hours',
}

type UsageCardLimitedResourceProps = {
  loading: boolean;
  resourceType: LimitedResourceType;
  usage?: number;
  limit?: number;
  showsUnlimitedText?: boolean;
};

const UsageCardLimitedResourceComp = ({
  loading,
  resourceType,
  usage,
  limit,
  showsUnlimitedText,
}: UsageCardLimitedResourceProps) => {
  if (loading) {
    return <S.LoadingUsageCard />;
  }

  if (usage == null) {
    // this (done loading but no usage data) shouldn't happen
    captureError(
      'Required usage data not passed to UsageCard',
      'UsageCardLimitedResource'
    );
    return (
      <S.ErrorUsageCardWrapper>
        <ErrorBox />
      </S.ErrorUsageCardWrapper>
    );
  }

  const usLocale = 'en-US';
  const usageLocaleString = usage.toLocaleString(usLocale);

  // undefined limit means no limit (unlimited) for this resource
  // in organization subscription privilege
  if (limit == null) {
    return (
      <S.UsageCard>
        <S.InfoTextWrapper>
          <S.UsageWrapper>
            <S.Usage percentage={0}>{usageLocaleString}</S.Usage>
            {showsUnlimitedText && <S.UsageLimit>/Unlimited</S.UsageLimit>}
          </S.UsageWrapper>
          <S.Title>{resourceType}</S.Title>
        </S.InfoTextWrapper>
      </S.UsageCard>
    );
  }

  const limitLocaleString = limit.toLocaleString(usLocale);

  // organization has some limit on this resource
  // show the usage progress with a progress bar
  const usagePercent = getUsagePercent(usage, limit);

  return (
    <S.UsageCard>
      <S.InfoTextWrapper>
        <S.UsageWrapper>
          <S.Usage
            percentage={
              resourceType === LimitedResourceType.USER ? 0 : usagePercent ?? 0
            }>
            {usageLocaleString}
          </S.Usage>
          <S.UsageLimit>/{limitLocaleString}</S.UsageLimit>
        </S.UsageWrapper>
        <S.Title>{resourceType}</S.Title>
      </S.InfoTextWrapper>
      {usagePercent != null && (
        <S.OuterBar>
          <S.InnerBar
            percentage={usagePercent ?? 0}
            color={
              resourceType === LimitedResourceType.USER
                ? globals.TEAL_500
                : S.getUsageColor(globals.TEAL_500, usagePercent ?? 0)
            }
          />
        </S.OuterBar>
      )}
    </S.UsageCard>
  );
};

export const UsageCardLimitedResource = memo(UsageCardLimitedResourceComp);
